import {
  type Box3,
  DirectionalLight,
  HemisphereLight,
  PMREMGenerator,
  Sphere,
  Vector3,
  type WebGLRenderTarget
} from "three";
import { RoomEnvironment } from "three/examples/jsm/environments/RoomEnvironment.js";

import type { Renderer } from "./Renderer";
import type { World } from "./world/World";

export interface LightingSettings {
  sun: { color: string; intensity: number; direction: [number, number, number] };
  sky: { skyColor: string; groundColor: string; intensity: number };
  environmentIntensity: number;
  shadowMapSize: number;
  shadowBias: number;
}

export class Lighting {
  private readonly sun: DirectionalLight;
  private readonly sky: HemisphereLight;
  private readonly sunDirection: Vector3;
  private readonly environment: WebGLRenderTarget;

  constructor(
    private readonly world: World,
    renderer: Renderer,
    settings: LightingSettings
  ) {
    this.sun = new DirectionalLight(settings.sun.color, settings.sun.intensity);
    this.sunDirection = new Vector3(...settings.sun.direction).normalize();
    this.sun.castShadow = true;
    this.sun.shadow.mapSize.set(settings.shadowMapSize, settings.shadowMapSize);
    this.sun.shadow.bias = settings.shadowBias;

    this.sky = new HemisphereLight(settings.sky.skyColor, settings.sky.groundColor, settings.sky.intensity);

    world.scene.add(this.sun, this.sun.target, this.sky);

    const pmrem = new PMREMGenerator(renderer.webgl);
    const room = new RoomEnvironment();
    this.environment = pmrem.fromScene(room, 0.04);
    room.dispose();
    pmrem.dispose();
    world.scene.environment = this.environment.texture;
    world.scene.environmentIntensity = settings.environmentIntensity;
  }

  // NOTE: tell the sun where to look for small baord (viewer) and for an expanding board
  setFocusArea(box: Box3) {
    // a sphere keeps the shadow area the same size however the camera turns
    const sphere = box.getBoundingSphere(new Sphere());
    const shadowCamera = this.sun.shadow.camera;
    shadowCamera.left = shadowCamera.bottom = -sphere.radius;
    shadowCamera.right = shadowCamera.top = sphere.radius;
    shadowCamera.far = sphere.radius * 4;
    shadowCamera.updateProjectionMatrix();

    this.sun.target.position.copy(sphere.center);
    this.sun.position.copy(sphere.center).addScaledVector(this.sunDirection, sphere.radius * 2);
  }

  dispose() {
    this.world.scene.remove(this.sun, this.sun.target, this.sky);
    this.world.scene.environment = null;
    this.environment.dispose();
    this.sun.dispose();
    this.sky.dispose();
  }
}
